/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { DataCollectionRules } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { MonitorClient } from "../monitorClient";
import {
  DataCollectionRuleResource,
  DataCollectionRulesListByResourceGroupNextOptionalParams,
  DataCollectionRulesListByResourceGroupOptionalParams,
  DataCollectionRulesListBySubscriptionNextOptionalParams,
  DataCollectionRulesListBySubscriptionOptionalParams,
  DataCollectionRulesListByResourceGroupResponse,
  DataCollectionRulesListBySubscriptionResponse,
  DataCollectionRulesGetOptionalParams,
  DataCollectionRulesGetResponse,
  DataCollectionRulesCreateOptionalParams,
  DataCollectionRulesCreateResponse,
  DataCollectionRulesUpdateOptionalParams,
  DataCollectionRulesUpdateResponse,
  DataCollectionRulesDeleteOptionalParams,
  DataCollectionRulesListByResourceGroupNextResponse,
  DataCollectionRulesListBySubscriptionNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing DataCollectionRules operations. */
export class DataCollectionRulesImpl implements DataCollectionRules {
  private readonly client: MonitorClient;

  /**
   * Initialize a new instance of the class DataCollectionRules class.
   * @param client Reference to the service client
   */
  constructor(client: MonitorClient) {
    this.client = client;
  }

  /**
   * Lists all data collection rules in the specified resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  public listByResourceGroup(
    resourceGroupName: string,
    options?: DataCollectionRulesListByResourceGroupOptionalParams
  ): PagedAsyncIterableIterator<DataCollectionRuleResource> {
    const iter = this.listByResourceGroupPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listByResourceGroupPagingPage(resourceGroupName, options);
      }
    };
  }

  private async *listByResourceGroupPagingPage(
    resourceGroupName: string,
    options?: DataCollectionRulesListByResourceGroupOptionalParams
  ): AsyncIterableIterator<DataCollectionRuleResource[]> {
    let result = await this._listByResourceGroup(resourceGroupName, options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listByResourceGroupNext(
        resourceGroupName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listByResourceGroupPagingAll(
    resourceGroupName: string,
    options?: DataCollectionRulesListByResourceGroupOptionalParams
  ): AsyncIterableIterator<DataCollectionRuleResource> {
    for await (const page of this.listByResourceGroupPagingPage(
      resourceGroupName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * Lists all data collection rules in the specified subscription.
   * @param options The options parameters.
   */
  public listBySubscription(
    options?: DataCollectionRulesListBySubscriptionOptionalParams
  ): PagedAsyncIterableIterator<DataCollectionRuleResource> {
    const iter = this.listBySubscriptionPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listBySubscriptionPagingPage(options);
      }
    };
  }

  private async *listBySubscriptionPagingPage(
    options?: DataCollectionRulesListBySubscriptionOptionalParams
  ): AsyncIterableIterator<DataCollectionRuleResource[]> {
    let result = await this._listBySubscription(options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listBySubscriptionNext(continuationToken, options);
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listBySubscriptionPagingAll(
    options?: DataCollectionRulesListBySubscriptionOptionalParams
  ): AsyncIterableIterator<DataCollectionRuleResource> {
    for await (const page of this.listBySubscriptionPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all data collection rules in the specified resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  private _listByResourceGroup(
    resourceGroupName: string,
    options?: DataCollectionRulesListByResourceGroupOptionalParams
  ): Promise<DataCollectionRulesListByResourceGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, options },
      listByResourceGroupOperationSpec
    );
  }

  /**
   * Lists all data collection rules in the specified subscription.
   * @param options The options parameters.
   */
  private _listBySubscription(
    options?: DataCollectionRulesListBySubscriptionOptionalParams
  ): Promise<DataCollectionRulesListBySubscriptionResponse> {
    return this.client.sendOperationRequest(
      { options },
      listBySubscriptionOperationSpec
    );
  }

  /**
   * Returns the specified data collection rule.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param dataCollectionRuleName The name of the data collection rule. The name is case insensitive.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    dataCollectionRuleName: string,
    options?: DataCollectionRulesGetOptionalParams
  ): Promise<DataCollectionRulesGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, dataCollectionRuleName, options },
      getOperationSpec
    );
  }

  /**
   * Creates or updates a data collection rule.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param dataCollectionRuleName The name of the data collection rule. The name is case insensitive.
   * @param options The options parameters.
   */
  create(
    resourceGroupName: string,
    dataCollectionRuleName: string,
    options?: DataCollectionRulesCreateOptionalParams
  ): Promise<DataCollectionRulesCreateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, dataCollectionRuleName, options },
      createOperationSpec
    );
  }

  /**
   * Updates part of a data collection rule.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param dataCollectionRuleName The name of the data collection rule. The name is case insensitive.
   * @param options The options parameters.
   */
  update(
    resourceGroupName: string,
    dataCollectionRuleName: string,
    options?: DataCollectionRulesUpdateOptionalParams
  ): Promise<DataCollectionRulesUpdateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, dataCollectionRuleName, options },
      updateOperationSpec
    );
  }

  /**
   * Deletes a data collection rule.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param dataCollectionRuleName The name of the data collection rule. The name is case insensitive.
   * @param options The options parameters.
   */
  delete(
    resourceGroupName: string,
    dataCollectionRuleName: string,
    options?: DataCollectionRulesDeleteOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, dataCollectionRuleName, options },
      deleteOperationSpec
    );
  }

  /**
   * ListByResourceGroupNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param nextLink The nextLink from the previous successful call to the ListByResourceGroup method.
   * @param options The options parameters.
   */
  private _listByResourceGroupNext(
    resourceGroupName: string,
    nextLink: string,
    options?: DataCollectionRulesListByResourceGroupNextOptionalParams
  ): Promise<DataCollectionRulesListByResourceGroupNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listByResourceGroupNextOperationSpec
    );
  }

  /**
   * ListBySubscriptionNext
   * @param nextLink The nextLink from the previous successful call to the ListBySubscription method.
   * @param options The options parameters.
   */
  private _listBySubscriptionNext(
    nextLink: string,
    options?: DataCollectionRulesListBySubscriptionNextOptionalParams
  ): Promise<DataCollectionRulesListBySubscriptionNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listBySubscriptionNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listByResourceGroupOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Insights/dataCollectionRules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResourceListResult
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listBySubscriptionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Insights/dataCollectionRules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResourceListResult
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Insights/dataCollectionRules/{dataCollectionRuleName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResource
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.dataCollectionRuleName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const createOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Insights/dataCollectionRules/{dataCollectionRuleName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResource
    },
    201: {
      bodyMapper: Mappers.DataCollectionRuleResource
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  requestBody: Parameters.body3,
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.dataCollectionRuleName
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const updateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Insights/dataCollectionRules/{dataCollectionRuleName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResource
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  requestBody: Parameters.body1,
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.dataCollectionRuleName
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Insights/dataCollectionRules/{dataCollectionRuleName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    204: {},
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.dataCollectionRuleName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listByResourceGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResourceListResult
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listBySubscriptionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DataCollectionRuleResourceListResult
    },
    default: {
      bodyMapper: Mappers.ErrorResponseCommonV2
    }
  },
  queryParameters: [Parameters.apiVersion12],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink
  ],
  headerParameters: [Parameters.accept],
  serializer
};
